import { useParams, Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import BackButton from '../../components/BackButton.jsx';
import api from '../../api/client.js';
import clsx from 'clsx';

const TOUCH_ICON = {
  visit: '🚗', call: '📞', email: '✉️', quote: '💲', photo: '📷', note: '✏️',
};

export default function RepDetail() {
  const { id } = useParams();
  const q = useQuery({
    queryKey: ['admin-rep', id],
    queryFn: () => api.get(`/admin/reps/${id}`).then(r => r.data.data),
  });

  if (q.isLoading) return <div className="text-muted">Loading…</div>;
  if (q.isError) return <div className="text-crimson">Failed to load rep — {q.error?.response?.data?.error || 'unknown error'}</div>;
  const d = q.data;
  const r = d.rep;

  return (
    <div className="space-y-4">
      <header>
        <div className="section-eyebrow">Manager · Rep detail</div>
        <div className="flex items-baseline justify-between gap-3 flex-wrap">
          <h1 className="text-2xl">{r.full_name}</h1>
          <BackButton fallback="/admin/dashboard" />
        </div>
        <div className="rule-crimson" />
        <div className="text-sm text-muted mt-1">
          {r.role}{r.branch_name && ` · ${r.branch_name}`}
          {r.email && <> · <a href={`mailto:${r.email}`} className="hover:text-crimson">{r.email}</a></>}
          {r.phone && <> · {r.phone}</>}
          {' · '}
          <span className={r.status === 'active' ? 'text-emerald-700' : 'text-crimson'}>{r.status}</span>
          {r.last_active_at && <> · last active {timeAgo(r.last_active_at)}</>}
        </div>
      </header>

      {/* KPI strip */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
        <Kpi
          label="Pipeline"
          value={fmtMoney(d.kpis.pipeline.weighted)}
          sub={`${d.kpis.pipeline.open_count} open · ${fmtMoney(d.kpis.pipeline.open_value)}`}
        />
        <Kpi
          label="Follow-ups"
          value={d.kpis.followups.open_count}
          sub={
            d.kpis.followups.overdue > 0
              ? <span className="text-crimson font-medium">{d.kpis.followups.overdue} overdue</span>
              : `${d.kpis.followups.due_this_week} due this week`
          }
          tone={d.kpis.followups.overdue > 0 ? 'warn' : 'normal'}
        />
        <Kpi
          label="This week"
          value={d.kpis.activity_week.notes}
          sub={`${d.kpis.activity_week.visits}v · ${d.kpis.activity_week.calls}c · ${d.kpis.activity_week.emails}e`}
        />
        <Kpi
          label="Coverage"
          value={d.kpis.coverage.total_owned > 0
            ? `${Math.round((d.kpis.coverage.touched_30d / d.kpis.coverage.total_owned) * 100)}%`
            : '—'}
          sub={`${d.kpis.coverage.touched_30d} of ${d.kpis.coverage.total_owned} in 30d`}
        />
      </div>

      {/* Activity trend */}
      <ActivityTrend trend={d.activity_trend} baseline={d.baseline_4w_avg} />

      {/* Pipeline + Follow-ups two columns */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <PipelineSection
          openDeals={d.open_deals}
          agingDeals={d.aging_deals}
          recentWins={d.recent_wins}
          recentLosses={d.recent_losses}
        />
        <FollowupsSection
          overdue={d.overdue_followups}
          upcoming={d.upcoming_followups}
          completed7d={d.kpis.followups.completed_7d}
        />
      </div>

      {/* Customer book */}
      <CustomerBook book={d.customer_book} coverage={d.kpis.coverage} />

      {/* Recap history */}
      <RecapHistory history={d.recap_history} />

      {/* Activity timeline */}
      <Timeline timeline={d.timeline} />
    </div>
  );
}

/* ---------- subcomponents ---------- */

function Kpi({ label, value, sub, tone }) {
  return (
    <div className={clsx('card p-3', tone === 'warn' && 'ring-1 ring-crimson')}>
      <div className="text-[10px] uppercase tracking-wider text-muted">{label}</div>
      <div className={clsx('text-xl font-bold', tone === 'warn' ? 'text-crimson' : 'text-navy')}>{value}</div>
      <div className="text-xs text-muted mt-1">{sub}</div>
    </div>
  );
}

function ActivityTrend({ trend, baseline }) {
  const max = Math.max(1, baseline, ...trend.map(t => t.total));
  const currentWeek = trend[trend.length - 1];
  const baselineDelta = baseline > 0
    ? Math.round(((currentWeek.total - baseline) / baseline) * 100)
    : null;

  return (
    <section className="card p-4">
      <div className="flex items-baseline justify-between flex-wrap gap-2">
        <div>
          <div className="section-eyebrow">Activity — last 12 weeks</div>
        </div>
        <div className="text-xs text-muted">
          4-week avg: <span className="font-medium text-navy">{baseline}</span>
          {baselineDelta != null && (
            <span className={clsx('ml-2 font-medium',
              baselineDelta < -25 ? 'text-crimson'
              : baselineDelta > 10 ? 'text-emerald-700'
              : 'text-muted')}>
              this week: {baselineDelta > 0 ? '+' : ''}{baselineDelta}%
            </span>
          )}
        </div>
      </div>
      <div className="rule-crimson" />
      <div className="space-y-1 mt-3">
        {trend.map((w, i) => {
          const pct = (n) => (n / max) * 100;
          const isCurrent = i === trend.length - 1;
          return (
            <div key={w.week_start} className="flex items-center gap-2 text-xs">
              <div className="w-16 text-muted tabular-nums">{shortWeek(w.week_start)}</div>
              <div className="flex-1 h-5 bg-panel/50 rounded relative overflow-hidden flex">
                {w.visits > 0 && <div className="bg-navy" style={{ width: `${pct(w.visits)}%` }} title={`${w.visits} visits`} />}
                {w.calls > 0 && <div className="bg-navy-300" style={{ width: `${pct(w.calls)}%` }} title={`${w.calls} calls`} />}
                {w.emails > 0 && <div className="bg-gold" style={{ width: `${pct(w.emails)}%` }} title={`${w.emails} emails`} />}
                {w.quotes > 0 && <div className="bg-crimson" style={{ width: `${pct(w.quotes)}%` }} title={`${w.quotes} quotes`} />}
                {w.other > 0 && <div className="bg-navy-100" style={{ width: `${pct(w.other)}%` }} title={`${w.other} other`} />}
              </div>
              <div className={clsx('w-8 text-right tabular-nums', isCurrent && 'font-semibold text-navy')}>
                {w.total}
              </div>
            </div>
          );
        })}
      </div>
      <div className="flex flex-wrap gap-3 text-[10px] text-muted mt-3">
        <span className="flex items-center gap-1"><span className="w-3 h-3 bg-navy rounded-sm inline-block" /> visits</span>
        <span className="flex items-center gap-1"><span className="w-3 h-3 bg-navy-300 rounded-sm inline-block" /> calls</span>
        <span className="flex items-center gap-1"><span className="w-3 h-3 bg-gold rounded-sm inline-block" /> emails</span>
        <span className="flex items-center gap-1"><span className="w-3 h-3 bg-crimson rounded-sm inline-block" /> quotes</span>
        <span className="flex items-center gap-1"><span className="w-3 h-3 bg-navy-100 rounded-sm inline-block" /> other</span>
      </div>
    </section>
  );
}

function PipelineSection({ openDeals, agingDeals, recentWins, recentLosses }) {
  return (
    <section className="card p-4">
      <div className="section-eyebrow">Pipeline</div>
      <div className="rule-crimson" />

      <SubHeader>Open deals</SubHeader>
      {openDeals.length === 0
        ? <Empty>No open deals.</Empty>
        : (
          <div className="space-y-1">
            {openDeals.map(o => (
              <DealRow key={o.id} deal={o} />
            ))}
          </div>
        )}

      {agingDeals.length > 0 && (
        <>
          <SubHeader>Aging (open &gt; 60d)</SubHeader>
          <div className="space-y-1">
            {agingDeals.map(o => (
              <DealRow key={o.id} deal={o} aging />
            ))}
          </div>
        </>
      )}

      {recentWins.length > 0 && (
        <>
          <SubHeader>Recent wins (90d)</SubHeader>
          <div className="space-y-1">
            {recentWins.map(o => {
              const Wrap = Link;
              const wp = { to: `/pipeline/${o.id}`, className: 'block text-xs flex items-baseline justify-between gap-2 py-1 hover:bg-panel rounded px-1 -mx-1' };
              return (
                <Wrap key={o.id} {...wp}>
                  <div className="flex-1 truncate">
                    <span className="text-emerald-700">✓</span> {o.name}
                    <CustDot row={o} />
                  </div>
                  <span className="font-medium tabular-nums">{fmtMoney(o.value_usd)}</span>
                </Wrap>
              );
            })}
          </div>
        </>
      )}

      {recentLosses.length > 0 && (
        <>
          <SubHeader>Recent losses (90d)</SubHeader>
          <div className="space-y-1">
            {recentLosses.map(o => {
              const Wrap = Link;
              const wp = { to: `/pipeline/${o.id}`, className: 'block text-xs flex items-baseline justify-between gap-2 py-1 hover:bg-panel rounded px-1 -mx-1' };
              return (
                <Wrap key={o.id} {...wp}>
                  <div className="flex-1 truncate">
                    <span className="text-crimson">✗</span> {o.name}
                    <CustDot row={o} />
                    {o.lost_reason && <div className="text-muted text-[11px] italic">{o.lost_reason}</div>}
                  </div>
                  <span className="text-muted tabular-nums">{fmtMoney(o.value_usd)}</span>
                </Wrap>
              );
            })}
          </div>
        </>
      )}
    </section>
  );
}

function DealRow({ deal, aging }) {
  const Wrap = Link;
  const wrapProps = { to: `/pipeline/${deal.id}`, className: 'block text-xs flex items-baseline justify-between gap-2 py-1 hover:bg-panel rounded px-1 -mx-1' };
  return (
    <Wrap {...wrapProps}>
      <div className="flex-1 truncate">
        <span className="font-medium">{deal.name}</span>
        <CustDot row={deal} />
        <div className="text-muted text-[11px] capitalize">
          {deal.stage}
          {deal.probability != null && ` · ${deal.probability}%`}
          {deal.age_days != null && (
            <span className={clsx(aging && 'text-crimson font-medium')}> · {deal.age_days}d old</span>
          )}
        </div>
      </div>
      <span className="font-medium tabular-nums whitespace-nowrap">{fmtMoney(deal.value_usd)}</span>
    </Wrap>
  );
}

function FollowupsSection({ overdue, upcoming, completed7d }) {
  return (
    <section className="card p-4">
      <div className="section-eyebrow">Follow-ups</div>
      <div className="rule-crimson" />

      <SubHeader tone={overdue.length > 0 ? 'warn' : 'normal'}>
        Overdue ({overdue.length})
      </SubHeader>
      {overdue.length === 0
        ? <Empty>None overdue. Nice.</Empty>
        : (
          <div className="space-y-1">
            {overdue.map(f => <FollowupRow key={f.id} f={f} overdue />)}
          </div>
        )}

      <SubHeader>Due this week ({upcoming.length})</SubHeader>
      {upcoming.length === 0
        ? <Empty>Nothing due in next 7 days.</Empty>
        : (
          <div className="space-y-1">
            {upcoming.map(f => <FollowupRow key={f.id} f={f} />)}
          </div>
        )}

      <div className="text-[11px] text-muted mt-3">
        ✓ {completed7d} completed in the last 7 days
      </div>
    </section>
  );
}

// Inline " · customer" cell. Falls back to the call-report's extracted name
// (flagged "unlinked") when the record isn't linked to a customer record.
function CustDot({ row }) {
  if (row.customer_name) return <span className="text-muted"> · {row.customer_name}</span>;
  if (row.source_customer_name) return (
    <span className="text-muted"> · {row.source_customer_name} <span className="text-[9px] px-1 rounded bg-amber-100 text-amber-800">unlinked</span></span>
  );
  return null;
}

function FollowupRow({ f, overdue }) {
  const Wrap = Link;
  const wrapProps = { to: `/followups/${f.id}`, className: clsx('block text-xs py-1 border-l-2 pl-2 hover:bg-panel rounded-r',
      overdue ? (f.urgency === 'high' ? 'border-crimson' : f.urgency === 'medium' ? 'border-gold' : 'border-navy-300') : 'border-transparent') };
  return (
    <Wrap {...wrapProps}>
      <div className="flex items-baseline justify-between gap-2">
        <span className="font-medium">{f.title}</span>
        <span className={clsx('whitespace-nowrap tabular-nums', overdue ? 'text-crimson font-semibold' : 'text-muted')}>
          {overdue ? `${f.days_overdue}d overdue` : new Date(f.due_date).toLocaleDateString()}
        </span>
      </div>
      <div className="text-muted text-[11px]">
        {f.customer_name
          ? f.customer_name
          : f.source_customer_name
            ? <span>{f.source_customer_name} <span className="text-[9px] px-1 rounded bg-amber-100 text-amber-800">unlinked</span></span>
            : <span className="italic">General task</span>}
        {' · '}<span className="capitalize">{f.urgency}</span>
      </div>
    </Wrap>
  );
}

function CustomerBook({ book, coverage }) {
  return (
    <section className="card p-4">
      <div className="flex items-baseline justify-between flex-wrap gap-2">
        <div className="section-eyebrow">Customer book</div>
        <div className="text-xs text-muted">
          {coverage.total_owned} customers ·
          {' '}<span className="text-emerald-700">{coverage.touched_30d}</span> in 30d ·
          {' '}<span className="text-gold">{coverage.stale_30_60}</span> 30–60d ·
          {' '}<span className="text-crimson">{coverage.stale_60_90 + coverage.stale_90_plus}</span> 60d+
        </div>
      </div>
      <div className="rule-crimson" />
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-3">
        <div>
          <SubHeader>Recently touched</SubHeader>
          {book.recently_touched.length === 0
            ? <Empty>No customer activity yet.</Empty>
            : (
              <div className="space-y-1">
                {book.recently_touched.map(c => (
                  <Link key={c.customer_id} to={`/customers/${c.customer_id}`}
                    className="block text-xs py-1 hover:text-crimson">
                    <div className="flex items-baseline justify-between gap-2">
                      <span className="font-medium truncate">{c.name}</span>
                      <span className="text-muted whitespace-nowrap">{c.days_since}d ago</span>
                    </div>
                    <div className="text-[11px] text-muted">{[c.city, c.state].filter(Boolean).join(', ') || '—'} · {c.touch_count} touches</div>
                  </Link>
                ))}
              </div>
            )}
        </div>
        <div>
          <SubHeader>Stale (no touch 30+ days)</SubHeader>
          {book.stale.length === 0
            ? <Empty>All caught up.</Empty>
            : (
              <div className="space-y-1">
                {book.stale.map(c => (
                  <Link key={c.customer_id} to={`/customers/${c.customer_id}`}
                    className="block text-xs py-1 hover:text-crimson">
                    <div className="flex items-baseline justify-between gap-2">
                      <span className="font-medium truncate">{c.name}</span>
                      <span className={clsx('whitespace-nowrap tabular-nums',
                        c.days_since > 90 ? 'text-crimson font-semibold'
                        : c.days_since > 60 ? 'text-gold font-medium'
                        : 'text-muted')}>{c.days_since}d</span>
                    </div>
                    <div className="text-[11px] text-muted">{[c.city, c.state].filter(Boolean).join(', ') || '—'}</div>
                  </Link>
                ))}
              </div>
            )}
        </div>
      </div>
    </section>
  );
}

function RecapHistory({ history }) {
  if (history.length === 0) {
    return (
      <section className="card p-4">
        <div className="section-eyebrow">Recap submissions (last 5 weeks)</div>
        <div className="rule-crimson" />
        <Empty>No recaps on file.</Empty>
      </section>
    );
  }
  return (
    <section className="card p-4">
      <div className="section-eyebrow">Recap submissions (last 5 weeks)</div>
      <div className="rule-crimson" />
      <div className="flex gap-2 mt-3 flex-wrap">
        {history.map(r => (
          <div key={r.week_start_date} className="bg-panel rounded p-2 text-xs text-center min-w-[80px]">
            <div className="text-muted">{shortWeek(r.week_start_date)}</div>
            <div className={clsx('font-semibold mt-1',
              r.status === 'submitted' ? 'text-emerald-700'
              : r.status === 'draft' ? 'text-gold'
              : 'text-crimson')}>
              {r.status === 'submitted' ? '✓ submitted'
                : r.status === 'draft' ? '… draft'
                : r.status}
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

function Timeline({ timeline }) {
  if (timeline.length === 0) {
    return (
      <section className="card p-4">
        <div className="section-eyebrow">Recent activity</div>
        <div className="rule-crimson" />
        <Empty>No recent activity.</Empty>
      </section>
    );
  }
  return (
    <section className="card p-4">
      <div className="section-eyebrow">Recent activity ({timeline.length})</div>
      <div className="rule-crimson" />
      <div className="space-y-1 mt-3">
        {timeline.map(t => (
          <TimelineRow key={`${t.type}-${t.id}`} t={t} />
        ))}
      </div>
    </section>
  );
}

function TimelineRow({ t }) {
  const icon =
    t.type === 'note' ? (TOUCH_ICON[t.subtype] || '✏️')
    : t.type === 'followup_done' ? '☑'
    : t.type === 'opp_won' ? '🏆'
    : t.type === 'opp_lost' ? '❌'
    : '•';
  const dest =
    t.type === 'note' ? `/notes/${t.id}`
    : t.customer_id ? `/customers/${t.customer_id}`
    : null;
  const Wrap = dest ? Link : 'div';
  return (
    <Wrap {...(dest ? { to: dest } : {})}
      className={clsx('block text-xs py-1.5 px-2 rounded', dest && 'hover:bg-panel')}>
      <div className="flex items-baseline justify-between gap-2">
        <div className="flex-1 truncate">
          <span className="mr-1">{icon}</span>
          <span className="text-navy">{summarize(t)}</span>
          <CustDot row={t} />
        </div>
        <span className="text-muted whitespace-nowrap">{timeAgo(t.at)}</span>
      </div>
    </Wrap>
  );
}

function summarize(t) {
  if (t.type === 'note') return t.summary || '(note)';
  if (t.type === 'followup_done') return `Completed: ${t.summary}`;
  if (t.type === 'opp_won') return `Won: ${t.summary}`;
  if (t.type === 'opp_lost') return `Lost: ${t.summary}`;
  return t.summary || '(activity)';
}

/* ---------- shared bits ---------- */

function SubHeader({ children, tone }) {
  return (
    <div className={clsx('text-[11px] uppercase tracking-wider mt-3 mb-1',
      tone === 'warn' ? 'text-crimson font-semibold' : 'text-muted')}>
      {children}
    </div>
  );
}

function Empty({ children }) {
  return <div className="text-muted text-xs italic py-1">{children}</div>;
}

/* ---------- formatters ---------- */

function fmtMoney(v) {
  const n = Number(v || 0);
  if (n === 0) return '$0';
  if (n >= 1_000_000) return '$' + (n / 1_000_000).toFixed(1) + 'M';
  if (n >= 1_000) return '$' + Math.round(n / 1000) + 'k';
  return '$' + Math.round(n);
}

function shortWeek(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  return `${d.getUTCMonth() + 1}/${d.getUTCDate()}`;
}

function timeAgo(iso) {
  if (!iso) return 'never';
  const ms = Date.now() - new Date(iso).getTime();
  const m = Math.floor(ms / 60000);
  if (m < 1) return 'just now';
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  if (d < 30) return `${d}d ago`;
  return new Date(iso).toLocaleDateString();
}
